import {
    BreadCrumbItem,
    BreadCrumbs as BreadCrumbsComponent,
} from '@eventbrite/breadcrumbs-infra';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

interface CityBrowseBreadCrumbItem {
    clientSideLink?: boolean;
}
interface BreadCrumbContainerProps {
    locationSlug: string;
    currentPlace: string;
    country: string;
    countrySlug: string;
    city?: string;
    citySlug?: string;
    borough?: string;
    boroughSlug?: string;
    placeType: string;
    showThingsToDoPhase2: boolean;
    locale: string;
    usesLanguageSubdirectory: boolean;
}

export const BreadCrumbs: React.FunctionComponent<BreadCrumbContainerProps> = (
    props,
) => {
    const {
        locationSlug,
        currentPlace,
        placeType = '',
        country,
        countrySlug,
        city,
        citySlug,
        borough,
        boroughSlug,
        showThingsToDoPhase2,
        locale,
        usesLanguageSubdirectory,
    } = props;

    const isOnline = country ? false : true;

    const breadcrumbItems = React.useMemo<
        BreadCrumbItem<CityBrowseBreadCrumbItem>[]
    >(() => {
        let items: BreadCrumbItem<CityBrowseBreadCrumbItem>[] = [
            {
                value: usesLanguageSubdirectory ? `/${locale}/` : '/',
                display: gettext('Home'),
            },
        ];

        if (showThingsToDoPhase2) {
            if (
                country &&
                ['neighbourhood', 'borough', 'locality', 'region'].includes(
                    placeType,
                )
            ) {
                items.push({
                    value: usesLanguageSubdirectory
                        ? `/${locale}/ttd/${countrySlug}/`
                        : `/ttd/${countrySlug}/`,
                    display: gettext('%(country)s', { country }),
                });
            }

            if (city && ['neighbourhood', 'borough'].includes(placeType)) {
                items.push({
                    value: usesLanguageSubdirectory
                        ? `/${locale}/ttd/${citySlug}/`
                        : `/ttd/${citySlug}/`,
                    display: gettext('%(city)s', { city }),
                });
            }

            if (borough && placeType === 'neighbourhood') {
                items.push({
                    value: usesLanguageSubdirectory
                        ? `/${locale}/ttd/${boroughSlug}/`
                        : `/ttd/${boroughSlug}/`,
                    display: gettext('%(borough)s', { borough }),
                });
            }
        }

        items = [
            ...items,
            ...(showThingsToDoPhase2
                ? [
                      {
                          value: usesLanguageSubdirectory
                              ? `/${locale}/ttd/${locationSlug}/`
                              : `/ttd/${locationSlug}/`,
                          display: isOnline
                              ? gettext('Online')
                              : gettext('Things to do in %(currentPlace)s', {
                                    currentPlace,
                                }),
                      },
                  ]
                : []),
            {
                value: usesLanguageSubdirectory
                    ? `/${locale}/d/${locationSlug}/events/`
                    : `/d/${locationSlug}/events/`,
                display: showThingsToDoPhase2
                    ? isOnline
                        ? gettext('Online Events')
                        : gettext('Events in %(currentPlace)s', {
                              currentPlace,
                          })
                    : currentPlace,
                clientSideLink: true,
            },
        ];

        return items;
    }, [
        locationSlug,
        currentPlace,
        country,
        countrySlug,
        city,
        citySlug,
        borough,
        boroughSlug,
        isOnline,
        placeType,
        showThingsToDoPhase2,
        locale,
        usesLanguageSubdirectory,
    ]);

    return (
        <BreadCrumbsComponent
            items={breadcrumbItems}
            separator={
                <span style={{ paddingLeft: '8px', paddingRight: '8px' }}>
                    /
                </span>
            }
            data-testid="city-browse-header"
            shouldIncludeMicroData={true}
            ariaLabel={gettext('breadcrumbs')}
        >
            {(item) =>
                item.clientSideLink ? (
                    <Link to={item.value} {...item.valueMicrodataProps}>
                        {item.displayWithMicroData}
                    </Link>
                ) : (
                    <a href={item.value} {...item.valueMicrodataProps}>
                        {item.displayWithMicroData}
                    </a>
                )
            }
        </BreadCrumbsComponent>
    );
};

interface AppState {
    location: {
        slug: string;
        currentPlace: string;
        country: string;
        countrySlug: string;
        city: string;
        citySlug: string;
        borough: string;
        boroughSlug: string;
        placeType: string;
    };
    app: {
        featureFlags: {
            [key: string]: boolean;
        };
        locale: string;
        uses_language_subdirectory: boolean;
    };
}

export const _mapStateToProps = (state: AppState) => ({
    locationSlug: state.location.slug,
    currentPlace: state.location.currentPlace,
    country: state.location.country,
    countrySlug: state.location.countrySlug,
    city: state.location.city,
    citySlug: state.location.citySlug,
    borough: state.location.borough,
    boroughSlug: state.location.boroughSlug,
    placeType: state.location.placeType,
    showThingsToDoPhase2: state.app.featureFlags.enable_things_to_do_phase_2,
    locale: state.app.locale,
    usesLanguageSubdirectory: state.app.uses_language_subdirectory,
});

export const BreadCrumbContainer = connect(_mapStateToProps)(BreadCrumbs);
